import { defineEvent, withPii } from "@amtana/module";
import { z } from "zod";

import { deliveryChannelSchema, plannedDeliverySchema } from "./notification-router.schemas.ts";

// The router's own facts (ADR-022 points 3 and 5, epic 26, S26.05/S26.06), one stream per
// original fact (`notification-router:<the fact's own event id>`): the Go worker's `plan`
// intent appends `deliveries_planned` once notification answered who hears the fact, with the
// intent's key as the idempotency key, so a retried call lands on the same row and the router
// fans it out into `deliver` intents exactly once.

export const NOTIFICATION_ROUTER = "notification-router";

export const deliveriesPlanned = withPii(
  defineEvent(
    NOTIFICATION_ROUTER,
    "deliveries_planned",
    z.object({ factEventId: z.string(), deliveries: z.array(plannedDeliverySchema) }),
  ),
  // `deliveryKey` (review group 3 security finding 3): for a `transactionalEmail` delivery
  // planned before this fix, it is `<eventId>:transactionalEmail:<recipient>`, the address a
  // substring of the whole value, never a path's own exact match alone — `redactPaths`
  // (`@amtana/eventing`) is substring-aware precisely so declaring it here reaches it.
  ["deliveries[].recipient", "deliveries[].deliveryKey"],
);

/** A delivery intent spent every retry (ADR-022 point 8, S26.09): the router appends this once
 * the Go worker's `deliver` intent dies, from its own `OnDead` (mirroring how audience-scan's
 * dead intents become `run.failed`/`batch.failed`, `services/worker/internal/pipelines`). The
 * router hears this fact back like any other (`routerFacts`) and routes it to Slack channels
 * that hear `delivery-failures`; a dead Slack post about a delivery failure never re-posts
 * itself (D485's loop guard), so this still carries `channel` so that rule can tell. */
export const deliveryFailed = withPii(
  defineEvent(
    NOTIFICATION_ROUTER,
    "delivery_failed",
    z.object({
      deliveryKey: z.string(),
      channel: deliveryChannelSchema,
      recipient: z.string(),
      attempts: z.number().int().nonnegative(),
      lastFailureCode: z.string().nullable(),
    }),
  ),
  // Review group 3 security finding 3: `deliveryKey` carries the same address as `recipient`
  // for a `transactionalEmail` delivery, embedded in a composite string.
  ["recipient", "deliveryKey"],
);

export const notificationRouterEvents = [deliveriesPlanned, deliveryFailed] as const;

// The test-only router fixture (D463: `notification-router-fixture`/`fixture_raised`) is gone
// (D54x): it let the whole router be proved end to end before any real rule answered a real
// delivery (ADR-022's own transition safety). That safety net is no longer needed now that every
// production fact routes for real; the same end-to-end shape is proved on `billing.credits_low`,
// a real fact already on the catalogue, instead.
